// src/utils/generateReport.js

import {
  Document,
  Packer,
  Paragraph,
  HeadingLevel,
  ImageRun,
  TextRun,
} from "docx";
import { saveAs } from "file-saver";

/** Decode a base64 Data-URL into a Uint8Array */
function b64toArrayBuffer(dataURL) {
  const [, base64] = dataURL.split(",");
  const binary     = atob(base64);
  const arr        = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    arr[i] = binary.charCodeAt(i);
  }
  return arr;
}

export async function generateReport(projectInfo, rooms) {
  // build sections array
  const sections = [];

  // ── Project Header ────────────────────────────────────────────────────────
  const headerChildren = [
    new Paragraph({
      text:    projectInfo.projectName || "Untitled Report",
      heading: HeadingLevel.TITLE,
    }),
    new Paragraph(`Assessor: ${projectInfo.assessor || ""}`),
    new Paragraph(`Type:     ${projectInfo.projectType || ""}`),
    new Paragraph(`Date:     ${projectInfo.dateTime   || ""}`),
  ];
  if (projectInfo.propertyPhoto) {
    const buf = b64toArrayBuffer(projectInfo.propertyPhoto);
    headerChildren.push(
      new Paragraph({
        children: [
          new ImageRun({
            data: buf,
            transformation: { width: 280, height: 280 },
          }),
        ],
      })
    );
  }
  sections.push({ children: headerChildren });

  // ── One Section per Room ───────────────────────────────────────────────────
  rooms.forEach((room, idx) => {
    const children = [];

    // Room title & metadata
    children.push(
      new Paragraph({
        text:    `Room ${idx + 1}: ${room.room}`,
        heading: HeadingLevel.HEADING_1,
      })
    );
    children.push(new Paragraph(`Floor:     ${room.floor}`));
    children.push(new Paragraph(`Direction: ${room.compassDirection}`));
    children.push(new Paragraph(`Area:      ${room.area}`));

    // Observations
    children.push(
      new Paragraph({ text: "Observations:", heading: HeadingLevel.HEADING_2 })
    );
    children.push(new Paragraph(room.observations || ""));

    // Observation Photos (280×280)
    (room.observationsPhotos || []).forEach((b64) => {
      const buf = b64toArrayBuffer(b64);
      children.push(
        new Paragraph({
          children: [
            new ImageRun({
              data: buf,
              transformation: { width: 280, height: 280 },
            }),
          ],
          spacing: { after: 200 },
        })
      );
    });

    // Lab Results
    if ((room.labResults || []).length) {
      children.push(
        new Paragraph({
          text:    "Lab Results:",
          heading: HeadingLevel.HEADING_2,
        })
      );
      room.labResults.forEach((lab) => {
        children.push(
          new Paragraph({
            bullet: { level: 0 },
            children: [
              new TextRun(`Sample ${lab.sampleSerial} (${lab.airSampleType}) → `),
              new TextRun(lab.moldName.join(", ")),
            ],
          })
        );
      });
    }

    // ── IAQ Block (values + each photo immediately after) ────────────────────
    if (room.iaqReadings) {
      const iq = room.iaqReadings;
      children.push(
        new Paragraph({
          text:    "Indoor Air Quality",
          heading: HeadingLevel.HEADING_2,
        })
      );

      const readings = [
        {
          text:  `Temp: ${iq.temperature || ""} °C   Humidity: ${iq.relativeHumidity || ""} %`,
          photo: iq.temperatureHumidityPicture,
        },
        {
          text:  `PM₂.₅: ${iq.pm25 || ""}   PM₁₀: ${iq.pm10 || ""}   Particles: ${iq.particleCount || ""}`,
          photo: iq.pmPicture,
        },
        {
          text:  `Formaldehyde: ${iq.formaldehyde || ""} ppm   TVOC: ${iq.tvoc || ""} ppm`,
          photo: iq.formaldehydeTvocPicture,
        },
        {
          text:  `CO₂: ${iq.co2 || ""} ppm   AQI: ${iq.aqi || ""}`,
          photo: iq.co2AqiPicture,
        },
      ];

      readings.forEach(({ text, photo }) => {
        // 1) the reading line
        children.push(new Paragraph(text));
        // 2) its associated photo, if present
        if (photo && photo.startsWith("data:image")) {
          const buf = b64toArrayBuffer(photo);
          children.push(
            new Paragraph({
              children: [
                new ImageRun({
                  data: buf,
                  transformation: { width: 280, height: 280 },
                }),
              ],
              spacing: { after: 200 },
            })
          );
        }
      });
    }

    // Recommendations & Requirements
    children.push(
      new Paragraph({
        text:    "Recommendations:",
        heading: HeadingLevel.HEADING_2,
      })
    );
    children.push(new Paragraph(room.recommendations || ""));
    children.push(
      new Paragraph({
        text:    "Remediation Requirements:",
        heading: HeadingLevel.HEADING_2,
      })
    );
    children.push(
      new Paragraph(
        `${room.requirements.requirement || ""} (${room.requirements.approxFt || ""} ft²)`
      )
    );

    sections.push({ children });
  });

  // ── Generate & Download ────────────────────────────────────────────────────
  const doc = new Document({
    creator:  projectInfo.assessor    || "",
    title:    projectInfo.projectName || "",
    sections,
  });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${projectInfo.projectName || "Report"}.docx`);
}
